/**
 * ComitePage - Gestión del Comité de Ética
 * Features:
 * - Lista de miembros del comité
 * - Agregar/remover miembros
 * - Roles en el comité
 * - Estadísticas de actividad
 */
import { useState, useEffect } from 'react';
import { 
  Plus, 
  Users, 
  UserCheck, 
  Shield, 
  TrendingUp,
  Mail,
  Trash2,
  Edit
} from 'lucide-react';
import { comiteAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Table,
  Loading,
  Alert,
  Modal,
} from '../../components/common';

const ComitePage = () => {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.rol === 'superadmin';

  // Estado
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);

  // Formulario de agregar miembro
  const [addForm, setAddForm] = useState({
    id_usuario: '',
    rol_comite: 'miembro',
  });

  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);
  const [usuarios, setUsuarios] = useState([]);

  // Cargar datos
  useEffect(() => {
    loadMiembros();
    loadEstadisticas();
  }, []);

  const loadMiembros = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await comiteAPI.getMiembros();
      setMiembros(response.data);
    } catch (err) {
      console.error('Error al cargar miembros:', err);
      setError(err.message || 'Error al cargar miembros');
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const response = await comiteAPI.getEstadisticas();
      setEstadisticas(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const loadUsuariosDisponibles = async () => {
    try {
      // Cargar usuarios que no son parte del comité
      const response = await comiteAPI.getUsuariosDisponibles();
      setUsuarios(response.data);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  // Handlers
  const handleOpenAddModal = () => {
    loadUsuariosDisponibles();
    setShowAddModal(true);
  };

  const handleAgregarMiembro = async (e) => {
    e.preventDefault();

    if (!addForm.id_usuario) {
      setError('Debes seleccionar un usuario');
      return;
    }

    try {
      setError(null);
      await comiteAPI.agregarMiembro(addForm);
      
      setSuccess('Miembro agregado al comité correctamente');
      setShowAddModal(false);
      setAddForm({ id_usuario: '', rol_comite: 'miembro' });
      loadMiembros();
      loadEstadisticas();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al agregar miembro:', err);
      setError(err.message || 'Error al agregar miembro');
    }
  };

  const handleRemoverMiembro = async (miembroId) => {
    if (!confirm('¿Estás seguro de remover este miembro del comité?')) {
      return;
    }

    try {
      setError(null);
      await comiteAPI.removerMiembro(miembroId);
      
      setSuccess('Miembro removido del comité');
      loadMiembros();
      loadEstadisticas();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al remover miembro:', err);
      setError(err.message || 'Error al remover miembro');
    }
  };

  const handleCambiarRol = async (miembroId, nuevoRol) => {
    try {
      setError(null);
      await comiteAPI.cambiarRol(miembroId, { rol_comite: nuevoRol });
      
      setSuccess('Rol actualizado correctamente');
      loadMiembros();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al cambiar rol:', err);
      setError(err.message || 'Error al cambiar rol');
    }
  };

  // Obtener info de rol
  const getRolInfo = (rol) => {
    const roles = {
      presidente: {
        label: 'Presidente',
        color: 'purple',
        icon: Shield,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
      },
      secretario: {
        label: 'Secretario',
        color: 'blue',
        icon: UserCheck,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
      },
      miembro: {
        label: 'Miembro',
        color: 'gray',
        icon: Users,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
      },
    };

    return roles[rol] || roles.miembro;
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'usuario',
      label: 'Miembro',
      render: (item) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <div>
            <span className="font-medium text-gray-900">
              {item.usuario?.nombre_completo}
            </span>
            <p className="text-sm text-gray-500">{item.usuario?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'rol',
      label: 'Rol en Comité',
      render: (item) => {
        const rolInfo = getRolInfo(item.rol_comite);
        const Icon = rolInfo.icon;
        
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rolInfo.bgColor} ${rolInfo.textColor}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {rolInfo.label}
          </span>
        );
      },
    },
    {
      key: 'fecha_incorporacion',
      label: 'Fecha Incorporación',
      render: (item) => (
        <span className="text-sm text-gray-600">
          {new Date(item.fecha_incorporacion).toLocaleDateString('es-ES')}
        </span>
      ),
    },
    {
      key: 'casos_asignados',
      label: 'Casos Asignados',
      render: (item) => (
        <span className="text-sm font-medium text-gray-900">
          {item.casos_asignados || 0}
        </span>
      ),
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (item) => (
        <div className="flex gap-2">
          {isSuperAdmin && (
            <>
              <select
                value={item.rol_comite}
                onChange={(e) => handleCambiarRol(item.id_miembro, e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="presidente">Presidente</option>
                <option value="secretario">Secretario</option>
                <option value="miembro">Miembro</option>
              </select>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoverMiembro(item.id_miembro)}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Comité de Ética</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestión de miembros y actividades del comité
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            variant="primary"
            onClick={handleOpenAddModal}
            icon={Plus}
          >
            Agregar Miembro
          </Button>
        )}
      </div>

      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Miembros</p>
                <p className="text-2xl font-bold">{estadisticas.total_miembros || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Casos Activos</p>
                <p className="text-2xl font-bold">{estadisticas.casos_activos || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Casos Resueltos</p>
                <p className="text-2xl font-bold">{estadisticas.casos_resueltos || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tiempo Promedio</p>
                <p className="text-2xl font-bold">
                  {estadisticas.tiempo_promedio_dias || 0}d
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabla de miembros */}
      <Card>
        {loading ? (
          <div className="p-8">
            <Loading size="lg" text="Cargando miembros del comité..." />
          </div>
        ) : miembros.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay miembros en el comité</p>
            {isSuperAdmin && (
              <Button
                variant="primary"
                onClick={handleOpenAddModal}
                className="mt-4"
              >
                Agregar Primer Miembro
              </Button>
            )}
          </div>
        ) : (
          <Table columns={columns} data={miembros} />
        )}
      </Card>

      {/* Modal: Agregar Miembro */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Agregar Miembro al Comité"
        size="md"
      >
        <form onSubmit={handleAgregarMiembro} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Usuario *
            </label>
            <select
              value={addForm.id_usuario}
              onChange={(e) => setAddForm({ ...addForm, id_usuario: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Selecciona un usuario</option>
              {usuarios.map((u) => (
                <option key={u.id_usuario} value={u.id_usuario}>
                  {u.nombre_completo} ({u.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol en el Comité *
            </label>
            <select
              value={addForm.rol_comite}
              onChange={(e) => setAddForm({ ...addForm, rol_comite: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="miembro">Miembro</option>
              <option value="secretario">Secretario</option>
              <option value="presidente">Presidente</option>
            </select>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <p className="text-sm text-blue-900">
              Los miembros del comité podrán revisar denuncias, investigar casos y tomar decisiones sobre resoluciones.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Agregar Miembro
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ComitePage;
